import * as duration from 'duration-fns';
import { cloneDeep } from 'lodash';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';

import { ISnListQueryParameters } from '@lib/api';
import { AddFieldDialogComponent, EditFieldDialogComponent, SnFormFieldConfig } from '@lib/form';
import { t } from '@lib/translation';

import { LprHotListAPI, SnLpr } from '../../api';
import { AddReasonOptionDialogComponent } from '../add-reason-option-dialog/add-reason-option-dialog.component';

@Component({
  selector: 'fs-manage-internal-hotlist-dialog',
  templateUrl: './manage-internal-hotlist-dialog.component.html',
  styleUrls: ['./manage-internal-hotlist-dialog.component.scss'],
})
export class ManageInternalHotlistDialogComponent implements OnInit, OnDestroy {
  public form = new UntypedFormGroup({});
  public reasonForm = new UntypedFormGroup({});
  public formFields: SnFormFieldConfig[] = [
    {
      templateOptions: {
        label: t('common.hotlist'),
      },
      wrappers: ['form-field-group'],
      fieldGroupClassName: 'hotlist-form-row',
      fieldGroup: [
        {
          key: 'name',
          type: 'input',
          className: 'hotlist-form-col-6',
          templateOptions: {
            label: t('common.name'),
            placeholder: t('common.enterName'),
            required: true,
          },
        },
        {
          key: 'deduplicationSources',
          type: 'http-select',
          className: 'hotlist-form-col-6',
          templateOptions: {
            label: t('vehicleIntelligence.hotlist.reconcileAgainst'),
            placeholder: t('vehicleIntelligence.hotlist.selectExternalHotlist'),
            description: t('vehicleIntelligence.hotlist.reconcileDescription'),
            multiple: true,
            search: true,
            valueProp: 'id',
            labelProp: 'name',
            fetchDataFn: (params: ISnListQueryParameters) =>
              this._lprHotlistAPI.getLprHotlists({
                ...params,
                type: 'External',
              }),
          },
        },
        {
          key: 'defaultExpirationDuration',
          type: 'input',
          className: 'hotlist-form-col-4',
          defaultValue: this.getDefaultDays(),
          templateOptions: {
            label: t('vehicleIntelligence.hotlist.daysUntilExpiration'),
            appearance: 'fill',
            type: 'number',
            min: 0,
          },
        },
        {
          key: 'disableDuplicates',
          type: 'checkbox',
          className: 'disable-duplicates-col',
          templateOptions: {
            label: t('vehicleIntelligence.hotlist.preventDuplicates'),
          },
        },
      ],
    },
    {
      templateOptions: {
        label: t('form.fields'),
      },
      wrappers: ['form-field-group'],
      fieldGroupClassName: 'hotlist-form-row',
    },
  ];
  public reasonFormFields: SnFormFieldConfig[] = [
    {
      templateOptions: {
        label: t('form.reasonOptions'),
      },
      wrappers: ['form-field-group'],
      fieldGroupClassName: 'hotlist-form-row',
      fieldGroup: [],
    },
  ];
  public formModel: any = {};
  public reasonFormModel: any = {};
  public isLoading: boolean = false;
  public fields = [];
  public reasonOptions = [];
  public displayedColumns: string[] = [
    'fieldName',
    'fieldType',
    'required',
    'validation',
    'minLength',
    'maxLength',
    'hideInList',
    'options',
    'actions',
  ];
  public dataSource = new MatTableDataSource();

  private readonly _destroy$ = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { item: SnLpr; canDelete: string },
    private readonly _dialog: MatDialog,
    private readonly _dialogRef: MatDialogRef<ManageInternalHotlistDialogComponent>,
    private readonly _cd: ChangeDetectorRef,
    private readonly _lprHotlistAPI: LprHotListAPI,
  ) {}

  public ngOnInit(): void {
    if (this.data?.item) {
      this.formModel = {
        ...this.data.item,
        defaultExpirationDuration: this.getDefaultDays(),
        deduplicationSources: this.data.item.deduplicationSources,
        disableDuplicates: this.data.item.properties.disableDuplicates,
      };
      this.dataSource.data = cloneDeep(this.data.item.properties['fields']) || [];
      this.reasonOptions = cloneDeep(this.data.item.properties['reasonOptions']) || [];
    }
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public updateField(editData: any, index: number): void {
    this._dialog
      .open(EditFieldDialogComponent, {
        data: editData,
      })
      .afterClosed()
      .pipe(takeUntil(this._destroy$))
      .subscribe((result) => {
        if (!result) {
          return;
        }
        this.dataSource.data[index] = result;
        this.dataSource.data = [...this.dataSource.data];
        this._cd.detectChanges();
      });
  }

  public addField(clonedData?: any): void {
    this._dialog
      .open(AddFieldDialogComponent, {
        data: clonedData,
      })
      .afterClosed()
      .pipe(takeUntil(this._destroy$))
      .subscribe((result) => {
        if (!result) {
          return;
        }
        this.dataSource.data.push(result);
        this.dataSource.data = [...this.dataSource.data];
        this._cd.detectChanges();
      });
  }

  public addReasonOptions(): void {
    this._dialog
      .open(AddReasonOptionDialogComponent)
      .afterClosed()
      .pipe(takeUntil(this._destroy$))
      .subscribe((result) => {
        if (!result) {
          return;
        }
        result.reasonOptions
          ?.map((reason) => reason.option)
          .forEach((res) => {
            this.reasonOptions.push(res);
          });
      });
  }

  public delete(): void {
    this._dialogRef.close('delete');
  }

  public submitForm(): void {
    this.formModel.fields = this.dataSource.data;
    this.formModel.reasonOptions = this.reasonOptions;
    this.formModel.defaultExpirationDuration = duration.toString({
      days: Number(this.formModel.defaultExpirationDuration),
    });
    this._dialogRef.close(this.formModel);
  }

  public getDefaultDays(): string {
    let expDays;
    if (this.data?.item?.defaultExpirationDuration) {
      expDays = duration.parse(this.data.item.defaultExpirationDuration)?.days;
    }
    return expDays ? expDays : '30';
  }

  public trackBy(index: number): number {
    return index;
  }

  public handleOptions(options: any[]): string {
    return options.map((o) => o.option).join(' ,');
  }

  public deleteField(index: number): void {
    this.dataSource.data.splice(index, 1);
    this.dataSource.data = [...this.dataSource.data];
  }

  public cloneField(index: number): void {
    if (this.dataSource.data[index]) {
      this.addField(this.dataSource.data[index]);
    }
  }

  public editField(index: number): void {
    if (this.dataSource.data[index]) {
      this.updateField(this.dataSource.data[index], index);
    }
  }

  public deleteReason(index: number): void {
    this.reasonOptions.splice(index, 1);
  }
}
